import React, { useEffect, useState } from 'react';
import { Copy, ExternalLink, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getEscrowDisplayStatus,
  isEscrowCompleted,
  normalizeEscrowStatus,
  resolveEscrowDisputeId,
} from '../../utils/escrowDisplayStatus';
import {
  fetchEscrowPartyWallets,
  resolveEscrowWalletAddresses,
} from '../../utils/escrowPartyWallets';
import {
  formatEscrowDenominationLabel,
  resolveEscrowCreationCurrency,
  resolveEscrowDenominationAmount,
  resolveEscrowSettlementUsd,
} from '../../utils/escrowDenomination';

/**
 * Shared escrow detail sheet (rows, explorer/dispute, optional XRPL release/cancel, footnote).
 */
export default function EscrowDetailModalBody({
  escrow,
  exchangeRate,
  onDispute,
  onReleaseEscrow,
  isReleasing = false,
}) {
  const escrowId = escrow.id || escrow.escrowId || escrow.xrplEscrowId || '';
  const formattedId = escrowId || '#ESC-N/A';
  const displayId = String(formattedId).replace(/^#/, '').toUpperCase();
  const displayStatus = getEscrowDisplayStatus(escrow);
  const status = displayStatus.label;
  const statusLower = (escrow.status || '').toLowerCase();
  const normalizedStatus = normalizeEscrowStatus(
    escrow.status || escrow.escrowStatus || escrow.state || '',
  );
  const isPendingEscrow = normalizedStatus === 'pending';
  const escrowCompleted = isEscrowCompleted(escrow);
  const createdDate = escrow.createdAt || escrow.created || '';
  const createdTimestamp = createdDate ? new Date(createdDate).getTime() : null;
  const timeSinceCreation = createdTimestamp ? (Date.now() - createdTimestamp) / 1000 : null;
  const RELEASE_DELAY_SECONDS = 40;
  const timeRemaining =
    timeSinceCreation != null ? Math.max(0, RELEASE_DELAY_SECONDS - timeSinceCreation) : 0;
  const canReleaseNow = timeRemaining === 0;
  const hasXrplEscrowId = !!(escrow.xrplEscrowId || escrow.xrpl_escrow_id);
  const canRelease =
    hasXrplEscrowId &&
    (statusLower === 'active' || statusLower === 'pending release') &&
    canReleaseNow;

  const xrpHashes = Array.from(
    new Set(
      [
        escrow.xrpHash,
        escrow.xrp_hash,
        escrow.xrplEscrowId,
        escrow.xrpl_escrow_id,
        ...(Array.isArray(escrow.xrpHashes) ? escrow.xrpHashes : []),
        ...(Array.isArray(escrow.xrpHashs) ? escrow.xrpHashs : []),
      ]
        .filter((value) => typeof value === 'string' && value.trim())
        .map((value) => value.trim()),
    ),
  );

  const partyEscrowId = resolveEscrowDisputeId(escrow);
  const walletsFromEscrow = resolveEscrowWalletAddresses(escrow);
  const alreadyHaveWallets = !!(walletsFromEscrow.payerWallet && walletsFromEscrow.counterpartyWallet);
  const alreadyHaveDenom =
    resolveEscrowDenominationAmount(escrow) != null && !!resolveEscrowCreationCurrency(escrow);
  const [fetchedParties, setFetchedParties] = useState({
    payerWallet: '',
    counterpartyWallet: '',
    currency: '',
    denominationAmount: null,
  });

  useEffect(() => {
    if (!partyEscrowId || (alreadyHaveWallets && alreadyHaveDenom)) {
      setFetchedParties({
        payerWallet: '',
        counterpartyWallet: '',
        currency: '',
        denominationAmount: null,
      });
      return undefined;
    }

    let cancelled = false;
    setFetchedParties({
      payerWallet: '',
      counterpartyWallet: '',
      currency: '',
      denominationAmount: null,
    });
    fetchEscrowPartyWallets(partyEscrowId)
      .then((parties) => {
        if (!cancelled) setFetchedParties(parties);
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedParties({
            payerWallet: '',
            counterpartyWallet: '',
            currency: '',
            denominationAmount: null,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [partyEscrowId, alreadyHaveWallets, alreadyHaveDenom]);

  const mergedEscrow = {
    ...escrow,
    ...(fetchedParties.currency ? { currency: fetchedParties.currency } : {}),
    ...(fetchedParties.denominationAmount != null
      ? { denominationAmount: fetchedParties.denominationAmount }
      : {}),
  };

  const creationCurrency = resolveEscrowCreationCurrency(mergedEscrow);
  const denominationAmount = resolveEscrowDenominationAmount(mergedEscrow);

  const walletAddress =
    fetchedParties.payerWallet || walletsFromEscrow.payerWallet || '—';
  const counterpartyAddress =
    fetchedParties.counterpartyWallet || walletsFromEscrow.counterpartyWallet || '—';

  const escrowAmountLabel = formatEscrowDenominationLabel(mergedEscrow);
  const principalUsd = resolveEscrowSettlementUsd(mergedEscrow) ?? 0;
  const settlementAsset = String(mergedEscrow.amount?.currency || 'RLUSD').toUpperCase();

  const xrpUsdRate = (() => {
    const xrpAmt = Number(mergedEscrow.amount?.xrp);
    if (Number.isFinite(xrpAmt) && xrpAmt > 0 && Number.isFinite(principalUsd) && principalUsd > 0) {
      return principalUsd / xrpAmt;
    }
    return exchangeRate || 0;
  })();

  const exchangeRateLabel = (() => {
    if (creationCurrency === 'XRP' && Number(xrpUsdRate) > 0) {
      return `1 XRP = $${Number(xrpUsdRate).toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })}`;
    }
    if (['RLUSD', 'USDT', 'USDC', 'USD'].includes(creationCurrency)) {
      return `1 ${creationCurrency} ≈ $1.00 USD`;
    }
    if (
      creationCurrency &&
      denominationAmount != null &&
      denominationAmount > 0 &&
      Number.isFinite(principalUsd) &&
      principalUsd > 0
    ) {
      const perUnit = principalUsd / denominationAmount;
      return `1 ${creationCurrency} = $${perUnit.toLocaleString('en-US', {
        minimumFractionDigits: perUnit < 0.01 ? 6 : 4,
        maximumFractionDigits: perUnit < 0.01 ? 6 : 4,
      })}`;
    }
    return '—';
  })();

  const feePercent =
    escrow.feePercent != null && escrow.feePercent !== ''
      ? Number(escrow.feePercent)
      : escrow.escrowFeePercent != null && escrow.escrowFeePercent !== ''
        ? Number(escrow.escrowFeePercent)
        : 5;
  const feeUsd = Number.isFinite(principalUsd) ? principalUsd * (feePercent / 100) : 0;

  const pickFiniteUsd = (...values) => {
    for (const value of values) {
      const num = Number(value);
      if (Number.isFinite(num) && num >= 0) return num;
    }
    return null;
  };
  const resolvedFeeUsd =
    pickFiniteUsd(escrow.creationFeeUsd, escrow.escrowFeeUsd, escrow.feeUsd) ?? feeUsd;
  const feeLabel = `$${resolvedFeeUsd.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} (${Number.isFinite(feePercent) ? feePercent : 5}%)`;

  const amountUsdPlusFee = (() => {
    const amountUsd = pickFiniteUsd(escrow.amountUsd, escrow.amount?.usd, principalUsd);
    return amountUsd != null ? amountUsd + resolvedFeeUsd : null;
  })();
  const totalEscrowedUsd =
    pickFiniteUsd(
      escrow.payableAmountUsd,
      escrow.totalPayableUsd,
      escrow.totalEscrowedUsd,
    ) ??
    amountUsdPlusFee ??
    NaN;
  const totalEscrowedLabel = Number.isFinite(totalEscrowedUsd)
    ? `$${totalEscrowedUsd.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} USD`
    : '—';

  const recipientUsd = Math.max(0, principalUsd - resolvedFeeUsd);
  const recipientLabel = `$${recipientUsd.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} USD`;

  const netFeeDisplay =
    escrow.networkFee != null && escrow.networkFee !== ''
      ? `${Number(escrow.networkFee).toLocaleString('en-US', {
          minimumFractionDigits: 5,
          maximumFractionDigits: 8,
        })} XRP`
      : '0.00001 XRP';

  const explorerHash =
    xrpHashes.find((h) => typeof h === 'string' && /^[0-9A-Fa-f]{64}$/.test(h)) || xrpHashes[0];
  const xrplAcct = escrow.xrplEscrowId || escrow.xrpl_escrow_id;
  const explorerUrl = explorerHash
    ? `https://xrpscan.com/tx/${explorerHash}`
    : xrplAcct
      ? `https://xrpscan.com/account/${xrplAcct}`
      : null;

  const statusBadgeVariant = escrowCompleted
    ? 'complete'
    : statusLower.includes('pending') || statusLower.includes('wait')
      ? 'pending'
      : statusLower.includes('complete') ||
          statusLower.includes('released') ||
          statusLower.includes('done')
        ? 'complete'
        : 'neutral';

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(displayId);
      toast.success('Escrow ID copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const openExplorer = () => {
    if (explorerUrl) {
      window.open(explorerUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('No explorer link available yet');
    }
  };

  const disclaimerAsset = settlementAsset;
  const disclaimerApprox = Number.isFinite(recipientUsd)
    ? recipientUsd.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0.00';

  const showSecondaryXRPL =
    typeof onReleaseEscrow === 'function' &&
    typeof onDispute === 'function' &&
    hasXrplEscrowId &&
    (statusLower === 'active' || statusLower === 'pending release');

  return (
    <>
      <div className="escrow-detail-sheet-rows">
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Escrow Id</span>
          <span className="escrow-detail-value escrow-detail-value--row">
            <span className="escrow-detail-id-link">{displayId}</span>
            <button
              type="button"
              className="escrow-detail-icon-btn"
              onClick={handleCopyId}
              aria-label="Copy escrow ID"
            >
              <Copy size={16} />
            </button>
          </span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Wallet Address</span>
          <span className="escrow-detail-value escrow-detail-value--truncate">{walletAddress}</span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Counterparty Address</span>
          <span className="escrow-detail-value escrow-detail-value--truncate">
            {counterpartyAddress}
          </span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Escrow Status</span>
          <span className="escrow-detail-value">
            <span
              className={`escrow-detail-status-badge escrow-detail-status-badge--${statusBadgeVariant}`}
            >
              {status}
            </span>
          </span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Escrow Amount</span>
          <span className="escrow-detail-value">{escrowAmountLabel}</span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Exchange Rate</span>
          <span className="escrow-detail-value">{exchangeRateLabel}</span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Network Fee</span>
          <span className="escrow-detail-value">{netFeeDisplay}</span>
        </div>
        <div className="escrow-detail-row">
          <span className="escrow-detail-label">Escrow Fee</span>
          <span className="escrow-detail-value">{feeLabel}</span>
        </div>
        <div className="escrow-detail-row escrow-detail-row--fee-sep escrow-detail-row--total">
          <span className="escrow-detail-label">Total Escrowed Amount</span>
          <span className="escrow-detail-value escrow-detail-value--strong">{totalEscrowedLabel}</span>
        </div>
        <div className="escrow-detail-row escrow-detail-row--recipient">
          <span className="escrow-detail-label">Recipient Gets</span>
          <span className="escrow-detail-value escrow-detail-value--strong">{recipientLabel}</span>
        </div>
        <div className="escrow-detail-row escrow-detail-row--last">
          <span className="escrow-detail-label">Estimated Arrival</span>
          <span className="escrow-detail-value">3–5 seconds</span>
        </div>
      </div>

      <div className="escrow-detail-modal-actions">
        <button
          type="button"
          className="escrow-detail-btn escrow-detail-btn--outline"
          onClick={openExplorer}
          disabled={!explorerUrl}
        >
          <ExternalLink size={16} aria-hidden />
          View on block Explorer
        </button>
        {!escrowCompleted &&
        !isPendingEscrow &&
        !showSecondaryXRPL &&
        typeof onDispute === 'function' ? (
          <button type="button" className="escrow-detail-btn escrow-detail-btn--primary" onClick={() => onDispute(escrow)}>
            Dispute
          </button>
        ) : null}
      </div>

      {showSecondaryXRPL && (
        <div className="escrow-detail-actions escrow-detail-actions--secondary">
          <button
            type="button"
            className="release-btn"
            onClick={() => {
              if (canReleaseNow && !isReleasing) {
                onReleaseEscrow(escrowId);
              }
            }}
            disabled={!canReleaseNow || isReleasing}
            aria-busy={isReleasing || undefined}
            style={{
              opacity: canReleaseNow && !isReleasing ? 1 : 0.6,
              cursor: canReleaseNow && !isReleasing ? 'pointer' : 'not-allowed',
            }}
          >
            {isReleasing
              ? 'Waiting...'
              : canRelease
                ? 'Release'
                : timeRemaining > 0
                  ? `Release (${Math.ceil(timeRemaining)}s)`
                  : 'Release'}
          </button>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => onDispute(escrow)}
          >
            Dispute
          </button>
        </div>
      )}

      <p className="escrow-detail-modal-footnote">
        <Info size={14} className="escrow-detail-modal-footnote-icon" aria-hidden />
        Recipient will receive at least {disclaimerApprox} USD equivalent in {disclaimerAsset} or the
        transaction will be refunded.
      </p>
    </>
  );
}
